// Subida del audio de GUION GRABADO del modo "Video con influencer" (V2).
//
// A diferencia del grabador del editor (que sube a `video-project-assets` vía
// `uploadProjectAsset` y exige un proyecto de video), este modo no tiene
// proyecto: el audio va al bucket `voces` (el mismo del clonado de voz —
// privado, path por empresa) bajo `<companyId>/guiones/<filename>`, y viaja al
// webhook como URL firmada. No se registra en ninguna tabla: es un insumo
// efímero de la generación, no un asset listable. Ver ADR-526.

import { supabase } from './supabase';

const BUCKET = 'voces';
// TTL de la URL firmada que se manda como `audio_url`: la generación demora
// hasta 15 min (poll de InterfazV2); 24 h da margen amplio para retries de n8n.
const SIGNED_URL_TTL_SEC = 60 * 60 * 24;

export interface GuionAudioUploadResult {
  /** URL firmada (TTL 24 h) — se manda como `audio_url` en el payload. */
  url: string;
  /** Path dentro del bucket `voces` (`<companyId>/guiones/<filename>`). */
  storagePath: string;
}

function getFileExtension(fileName: string): string {
  const m = fileName.match(/\.([a-zA-Z0-9]+)$/);
  return m ? m[1].toLowerCase() : 'webm';
}

/**
 * Sube el audio del guion y devuelve la URL firmada + path. Tira Error con
 * mensaje user-facing si falla la subida o la firma.
 */
export async function uploadGuionAudio(
  file: File,
  companyId: string,
): Promise<GuionAudioUploadResult> {
  const ext = getFileExtension(file.name);
  const path = `${companyId}/guiones/${Date.now()}-${Math.random()
    .toString(36)
    .slice(2, 8)}.${ext}`;

  const { error: uploadError } = await supabase.storage
    .from(BUCKET)
    .upload(path, file, {
      cacheControl: '3600',
      upsert: false,
      contentType: file.type || `audio/${ext}`,
    });
  if (uploadError) {
    console.error('[guionAudioUpload] upload', uploadError);
    throw new Error('No pudimos subir el audio. Intentá de nuevo.');
  }

  const { data, error: signError } = await supabase.storage
    .from(BUCKET)
    .createSignedUrl(path, SIGNED_URL_TTL_SEC);
  if (signError || !data?.signedUrl) {
    console.error('[guionAudioUpload] signedUrl', signError);
    throw new Error('No pudimos preparar el audio. Intentá de nuevo.');
  }

  return { url: data.signedUrl, storagePath: path };
}
